import clientConfig from '../clientConfig';
import getPreviewModeFromPath from './preview/getPreviewModeFromPath';

export function getLocaleSegmentIndex(previewMode: boolean, rootMode: boolean) {
  if (rootMode) {
    return previewMode ? 2 : 0;
  }

  return previewMode ? 3 : 1;
}

export function localeInPath(path: string) {
  const reqPath = (path || '/').toLowerCase();
  const allowedLanguages = [...clientConfig.i18n.whitelist, 'cimode'];

  const { previewMode, previewTenantDomain } = getPreviewModeFromPath(path);
  const isPreviewPath = !!previewMode && !!previewTenantDomain;

  const segments = reqPath.slice(1).split('/');

  const localeIndex = getLocaleSegmentIndex(isPreviewPath, clientConfig.root);

  if (segments.length <= localeIndex) {
    return '';
  }

  const potentialLocale = segments[localeIndex];
  const lngIndex = allowedLanguages.indexOf(potentialLocale);

  if (lngIndex === -1) {
    return '';
  }

  // Additional validation: if root mode and no preview, check that the locale
  // is not part of an invalid path structure (e.g., repeated segments)
  if (clientConfig.root && !isPreviewPath && localeIndex === 0) {
    // Check if path has suspicious repeated segments that might indicate
    // an invalid path structure where locale should not be recognized
    const pathAfterLocale = segments.slice(1);
    if (pathAfterLocale.length > 0) {
      // Count occurrences of each segment to detect repeated patterns
      const segmentCounts = new Map<string, number>();
      for (const segment of pathAfterLocale) {
        segmentCounts.set(segment, (segmentCounts.get(segment) ?? 0) + 1);
      }

      // If there are many repeated segments, the path might be invalid
      // and the locale should not be recognized
      const maxCount = Math.max(...Array.from(segmentCounts.values()));
      if (maxCount > 2) {
        return '';
      }
    }
  }

  return allowedLanguages[lngIndex];
}

export function localizeRoutePath(path: string) {
  if (path.search(':lng') > -1) {
    const allowedLanguages = [...clientConfig.i18n.whitelist, 'cimode'].join(
      '|',
    );
    return path.replace(':lng', `:lng(${allowedLanguages})?`);
  }

  return path;
}

export function localizePath(path: string, lng = '', primaryLng = '') {
  let pathname = path;
  if (localeInPath(pathname)) {
    pathname = deLocalizePath(pathname);
  }

  if (!lng || lng.toLowerCase() === primaryLng) {
    return pathname;
  }

  const { previewMode, previewTenantDomain } = getPreviewModeFromPath(pathname);
  const isPreviewPath = !!previewMode && !!previewTenantDomain;

  const pathArr = pathname
    .slice(1)
    .split('/')
    .filter((s) => s.length);

  const localeIndex = getLocaleSegmentIndex(isPreviewPath, clientConfig.root);
  pathArr.splice(localeIndex, 0, lng.toLowerCase());
  return '/' + pathArr.join('/') + '/';
}

export function deLocalizePath(path: string) {
  const pathLng = localeInPath(path);

  if (!pathLng) {
    return path;
  }

  const { previewMode, previewTenantDomain } = getPreviewModeFromPath(path);
  const isPreviewPath = !!previewMode && !!previewTenantDomain;

  // Process path the same way as localeInPath to ensure consistent indexing
  const segments = path.slice(1).split('/');
  const localeIndex = getLocaleSegmentIndex(isPreviewPath, clientConfig.root);

  // Remove the locale segment if it exists at the expected index
  if (
    localeIndex < segments.length &&
    segments[localeIndex]?.toLowerCase() === pathLng.toLowerCase()
  ) {
    segments.splice(localeIndex, 1);
  }

  // Filter empty strings and join
  const filteredSegments = segments.filter((s) => s.length);
  return '/' + filteredSegments.join('/');
}
